import {
    Document, TableRow, TableCell, Table, Paragraph, VerticalAlign, AlignmentType, WidthType, ShadingType,
    Header, TextRun, PageNumber, Footer, HeadingLevel, UnderlineType, ExternalHyperlink, Packer, SectionType,
    TableOfContents, StyleLevel, convertInchesToTwip
} from 'docx'

import {saveAs} from 'file-saver'



const tableWithNoHeaderCellAndEqualWidthFormat = (totalColumnCounts=1) => {
    return {
        alignment: AlignmentType.CENTER,
        verticalAlign: VerticalAlign.CENTER,
        width: {
            size: 100/totalColumnCounts,
            type: WidthType.PERCENTAGE,
        },
        margins: {
            left: convertInchesToTwip(0.05),
            right: convertInchesToTwip(0.05),
            top: convertInchesToTwip(0.05),
            bottom: convertInchesToTwip(0.05),
        },
    }
}

const tableBorders = {
    top: { style: "single", size: 1, color: "D3D3D3" },
    bottom: { style: "single", size: 1, color: "D3D3D3" },
    left: { style: "single", size: 1, color: "D3D3D3" },
    right: { style: "single", size: 1, color: "D3D3D3" },
    insideHorizontal: { style: "single", size: 1, color: "D3D3D3" },
    insideVertical: { style: "single", size: 1, color: "D3D3D3" },
}

const mainHeadingFormatting = {
    pageBreakBefore: true,
    heading: HeadingLevel.HEADING_2,
    alignment: AlignmentType.CENTER,
    spacing: {
        before: 100, 
        after: 600,
        line: 360,
    },
    shading: {
        type: ShadingType.SOLID,
        color: "4472C4",
    },
    border: {
        top: { style: "none", size: 0, color: "4472C4", space: 8 },
        bottom: { style: "none", size: 0, color: "4472C4", space: 8 },
    },
}

// Common helpers for building document content
const createSectionHeading = (text) =>
    new Paragraph({
        ...mainHeadingFormatting,
        children: [
            new TextRun({
                text,
                color: "FFFFFF",
                bold: true,
            }),
        ],
    })

const createTextParagraph = ({
    text,
    alignment = AlignmentType.JUSTIFIED,
    spacing,
    bullet,
    bold = false,
    italics = false,
    color,
} = {}) =>
    new Paragraph({
        style: 'MyNormalStyle',
        alignment,
        ...(spacing ? { spacing } : {}),
        ...(bullet ? { bullet } : {}),
        children: [
            new TextRun({
                text,
                ...(bold ? { bold: true } : {}),
                ...(italics ? { italics: true } : {}),
                ...(color ? { color } : {}),
            }),
        ],
    })

const createBulletParagraph = (text) =>
    createTextParagraph({
        text,
        alignment: AlignmentType.JUSTIFIED,
        bullet: { level: 0 },
    })

const createPlaceholderParagraph = ({
    text = '...',
    alignment = AlignmentType.JUSTIFIED,
} = {}) =>
    createTextParagraph({
        text,
        alignment,
        italics: true,
        color: "0000FF",
    })

// Common cell margin presets
const HEADER_AND_REF_CELL_MARGINS = {
    left: convertInchesToTwip(0.05),
    right: convertInchesToTwip(0.05),
    top: convertInchesToTwip(0.05),
    bottom: convertInchesToTwip(0.05),
}

const FEATURE_CELL_MARGINS = {
    left: convertInchesToTwip(0),
    right: convertInchesToTwip(0),
    top: convertInchesToTwip(0),
    bottom: convertInchesToTwip(0),
}

// Helpers for common label/value cells used in information tables
const createLabelCell = ({
    text,
    totalColumns,
    widthPercent,
}) => new TableCell({
    ...tableWithNoHeaderCellAndEqualWidthFormat(totalColumns),
    columnSpan: 1,
    width: { size: widthPercent, type: WidthType.PERCENTAGE },
    children: [
        createTextParagraph({
            text,
            alignment: AlignmentType.LEFT,
            bold: true,
        }),
    ],
})

const createValueCell = ({
    text = '...',
    totalColumns,
    widthPercent,
}) => new TableCell({
    ...tableWithNoHeaderCellAndEqualWidthFormat(totalColumns),
    columnSpan: 1,
    width: { size: widthPercent, type: WidthType.PERCENTAGE },
    children: [
        createPlaceholderParagraph({
            text,
            alignment: AlignmentType.JUSTIFIED,
        }),
    ],
})


const reportObjective = () => {
    const elements = [
        createSectionHeading("Report Objective"),
        createTextParagraph({
            text: "Objective and Scope of Search",
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 300, after: 300 },
            bold: true,
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "The objective of this search is to perform a comprehensive Patent and Non-Patent Literature (NPL) search to identify references that fall within the same or substantially similar technical field as the subject patent ",
                }),
                new TextRun({
                    text: "(US XXXXXXXX)",
                    color: "0000FF",
                    italics: true,
                }),
                new TextRun({
                    text: ". Subject to appropriate legal review, these references may be construed as potential prior art.",
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            spacing: {before: 300, after: 300},
            children: [
                new TextRun({
                    text: "Scope of Analysis",
                    bold: true,
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "References identified include those exhibiting one or more technical features overlapping with the key features of the subject patent. Such references are considered based on the principle that they may be combined with other references to form relevant prior art under applicable patentability standards.",
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "The references included in this report may contain additional features or elements not found in the subject patent. They have been provided for the client’s information to present a broader view of the technological landscape.",
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "Patent references cited on the face of the subject patent and their family members have been excluded from the search. Similarly, Non-Patent Literature cited on the face of the subject patent is excluded from the scope of this search.",
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "For further details, please refer to the Conditions and Limitations of Search.",
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "We trust the results of this search will assist you in evaluating the position of the subject patent. Please feel free to reach out with any additional questions.",
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            alignment: AlignmentType.JUSTIFIED,
            spacing: {before: 300, after: 100},
            children: [
                new TextRun({
                    text: "Key Dates of the Subject Patent",
                    bold: true,
                }),
            ]
        }),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            children: [
                                new Paragraph({
                                    style: 'MyTableStyle',
                                    children: [
                                        new TextRun({
                                            text: "Priority Date: ",
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Enter Priority Date`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            children: [
                                new Paragraph({
                                    style: 'MyTableStyle',
                                    children: [
                                        new TextRun({
                                            text: "Filing Date: ",
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Enter Filing Date`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            children: [
                                new Paragraph({
                                    style: 'MyTableStyle',
                                    children: [
                                        new TextRun({
                                            text: "Issue Date: ",
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Enter Issue Date`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
            ],
        }),
    ]
    return elements
}

const focusOfSearch = (features) => {
    const elements = [
        createSectionHeading("Focus of Search / Claims"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Claims Searched`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                ...features.map((feature, index) => (
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.JUSTIFIED,
                                        bullet: {
                                            level: 0,
                                        },
                                        children: [
                                            new TextRun({
                                                text: `Claim ${feature.featureNum}: ${feature.featureText}`,
                                            }),
                                        ],
                                    })
                                )),
                            ],
                        }),                        
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Focus of the Search`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Enter a brief description of the focus of the search, e.g., key features, technical aspects, etc.`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),                        
                    ]
                }),
            ],
        }),
    ]
    return elements
}

const searchAnalysisSummary = (features) => {
    const elements = [
        createSectionHeading("Search Analysis Summary"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 3,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Observation Analysis (Prior Art analysis)`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),                        
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 2,
                            width: {
                                size: 80,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Strong art available for target claims`,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            width: {
                                size: 20,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Yes/No`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 2,
                            width: {
                                size: 80,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Relevant art is available for target claims`,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            width: {
                                size: 20,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Yes/No`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 2,
                            width: {
                                size: 80,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Prior art is not available for target claims in the given time`,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            width: {
                                size: 20,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Yes/No`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 3,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Claim Chart Type`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),                        
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Claim`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Type of Analysis`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Conclusion`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                ...features.map((feature, index) => {
                    return new TableRow({
                        height: {
                            value: convertInchesToTwip(0.5),
                            rule: "atLeast",
                        },
                        children: [
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                                width: {
                                    size: 10,
                                    type: WidthType.PERCENTAGE,
                                },
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `${feature.featureNum}`,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                                width: {
                                    size: 50,
                                    type: WidthType.PERCENTAGE,
                                },
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `Anticipation or Obviousness`,
                                                italics: true,
                                                color: "0000FF",
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                                width: {
                                    size: 40,
                                    type: WidthType.PERCENTAGE,
                                },
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `Yes/No`,
                                                italics: true,
                                                color: "0000FF",
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                        ]
                    })
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 3,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Critical Observation (Issues Checklist)`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Red – Issues Spotted, Grey: Currently, no issues spotted in the given timeline`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),                        
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 2,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Section`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Conclusion`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 2,
                            width: {
                                size: 80,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Section Reference...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            width: {
                                size: 20,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Yes/No`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            columnSpan: 2,
                            width: {
                                size: 80,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Section Reference...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(3),
                            width: {
                                size: 20,
                                type: WidthType.PERCENTAGE,
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Yes/No`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                })
            ],
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            spacing: {before: 300, after: 100},
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "Note: Observations are preliminary suggestions by Riahi Patents Inc. and are not binding on claim scope. The observations are not legal opinions and should not be considered legal opinions under any circumstances. MS reserves the right to dispute or disregard the analysis provided by the Riahi Patents Team.",
                    italics: true,
                }),
            ]
        }),
        new Paragraph({
            style: 'MyNormalStyle',
            spacing: {before: 300, after: 100},
            alignment: AlignmentType.JUSTIFIED,
            children: [
                new TextRun({
                    text: "Searcher Comment",
                    bold: true,
                    underline: {
                        type: UnderlineType.SINGLE,
                        color: "000000",
                    },
                }),
                new TextRun({
                    text: ": ",
                }),
                new TextRun({
                    text: `Enter any additional comments or observations from the searcher here...`,
                    italics: true,
                    color: "0000FF",
                }),
            ]
        }),
    ]
    return elements
}

const deliverables = () => {
    const elements = [
        createSectionHeading("Deliverables"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.4),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 30, type: WidthType.PERCENTAGE},
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `List of Deliverables`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 70, type: WidthType.PERCENTAGE},
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Link / Attachments`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 30, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `1. `,
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Search Report`,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 70, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Enter link or attachment for the search report here...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 30, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `2. `,
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Claim Chart`,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 70, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Enter link or attachment for the claim chart here...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 30, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `3. `,
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Search Strategy Document`,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(2),
                            columnSpan: 1,
                            width: {size: 70, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `Enter link or attachment for the search strategy document here...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
            ],
        }),
    ]
    return elements
}

const patentInformation = () => {
    const elements = [
        createSectionHeading("Patent Information"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Patent No.', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Application No.', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Title', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Priority', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Inventors', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Filed', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Assignee', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Publication No.', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Claims', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Publication Date', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Ind Claims', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Issued', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Family', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Expires (considering PTA as per USPTO)', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Licenses', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Reexam Request', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        createLabelCell({ text: 'Litigations', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 40 }),
                        createLabelCell({ text: 'Search Advice', totalColumns: 4, widthPercent: 20 }),
                        createValueCell({ totalColumns: 4, widthPercent: 20 }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 4,
                            width: {size: 100, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `Matter Information`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 20, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `MS Attorney`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 40, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 20, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `eTeam Member`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 20, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
            ],
        }),
    ]
    return elements
}

const searchDatesAndReferenceInformation = (features) => {
    const elements = [
        createSectionHeading("Search Dates and Reference Information"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 25, type: WidthType.PERCENTAGE},
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Search Dates`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 3,
                            width: {size: 75, type: WidthType.PERCENTAGE},
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Pre-AIA`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 25, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Claims`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 25, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: ` `,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 25, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: ` `,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                            columnSpan: 1,
                            width: {size: 25, type: WidthType.PERCENTAGE},
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: ` `,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                ...features.map((feature, index) => {
                    return new TableRow({
                        height: {
                            value: convertInchesToTwip(0.5),
                            rule: "atLeast",
                        },
                        children: [
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `${feature.featureNum}`,
                                                bold: true,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                                children: [
                                    createPlaceholderParagraph({
                                        alignment: AlignmentType.CENTER,
                                    }),
                                ],
                            }),
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                                children: [
                                    createPlaceholderParagraph({
                                        alignment: AlignmentType.CENTER,
                                    }),
                                ],
                            }),
                            new TableCell({
                                ...tableWithNoHeaderCellAndEqualWidthFormat(4),
                                children: [
                                    createPlaceholderParagraph({
                                        alignment: AlignmentType.CENTER,
                                    }),
                                ],
                            }),
                        ]
                    })
                }),
            ],
        }),
    ]
    return elements
}

const prosecutionHistoryAnalysis = () => {
    const elements = [
        createSectionHeading("Prosecution History Analysis"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `Restriction/Election`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `Terminal Disclaimer`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `Interview Summary`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `General Characterizations made about the invention during prosecution:`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `The applicant's statement about the cited references in responses to USPTO OA`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `Reasons for Allowance (OR) Arguments/Amendments leading to Allowance`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(1),
                            columnSpan: 1,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `Additional Information`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.JUSTIFIED,
                                    children: [
                                        new TextRun({
                                            text: `...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
            ],
        }),
    ]
    return elements
}

const listOfReferencesIdentified = (documents) => {
    const elements = [
        createSectionHeading("List of References Identified"),
        createTextParagraph({
            text: "Add Text Here...",
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 300, after: 200 },
            italics: true,
            color: "0000FF",
        }),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `S.No`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Reference Mapped`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Reference Title`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Assignee`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Filing Date`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Issue Date`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 1,
                            shading: {
                                type: ShadingType.SOLID,
                                color: "CCEEFF",
                            },
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Qualifying Statute`,
                                            bold: true,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
                ...documents.map((document, index) => {
                    return new TableRow({
                        height: {
                            value: convertInchesToTwip(0.5),
                            rule: "atLeast",
                        },
                        children: [
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `Ref ${index + 1}`,
                                                bold: true,
                                            }),
                                        ],
                                    })
                                ],
                            }),
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            document.features[0].classification==="PD" ?
                                            new ExternalHyperlink({
                                                children: [
                                                    new TextRun({
                                                        text: `${document.documentNumber}`,
                                                        style: "Hyperlink",
                                                    }),
                                                ],
                                                link: `https://patents.google.com/patent/${document.documentNumber}/en?oq=${document.documentNumber}`,
                                            })
                                            :
                                            new ExternalHyperlink({
                                                children: [
                                                    new TextRun({
                                                        text: `${document.features[0].documentTitle}`,
                                                        style: "Hyperlink",
                                                    }),
                                                ],
                                                link: `${document.documentNumber}`,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `${document.features[0].documentTitle || "..."}`,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `${document.features[0].assignees || "..."}`,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `${document.features[0].submissionDate ? `${new Date(document.features[0].submissionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : "..."}`,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `${document.features[0].publicationDate ? `${new Date(document.features[0].publicationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : "..."}`,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        style: 'MyNormalStyle',
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: `...`,
                                                italics: true,
                                                color: "0000FF",
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                        ]
                    })
                }),
                new TableRow({
                    height: {
                        value: convertInchesToTwip(0.5),
                        rule: "atLeast",
                    },
                    children: [
                        new TableCell({
                            ...tableWithNoHeaderCellAndEqualWidthFormat(7),
                            columnSpan: 7,
                            children: [
                                new Paragraph({
                                    style: 'MyNormalStyle',
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new TextRun({
                                            text: `Note: `,
                                            bold: true,
                                        }),
                                        new TextRun({
                                            text: `Enter additional notes here...`,
                                            italics: true,
                                            color: "0000FF",
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    ]
                }),
            ],
        }),
    ]
    return elements
}

const referenceKeyMatrixTable= (data) => {
    // Build header labels
    const headingRow = [
        'Reference',
        ...data.project.features.map((feature) => `Claim Element ${feature.featureNum}`),
    ]

    // Precompute project feature numbers once for all rows
    const projectFeaturesArray = Array.from(
        { length: data.project.features.length },
        (_, index) => `${index + 1}`,
    )

    const buildUniqueDocumentFeaturesArray = (features) => {
        const documentFeaturesArray = features.map((f) => f.feature)
        const documentFeaturesSet = new Set(documentFeaturesArray)
        return Array.from(documentFeaturesSet)
    }

    const elements = [
        createSectionHeading("Reference Key Matrix Table"),
        new Table({
            alignment: AlignmentType.CENTER,
            borders: tableBorders,
            rows: [
                // Header row
                new TableRow({
                    children: [
                        ...headingRow.map(element => {
                            return new TableCell({
                                margins: HEADER_AND_REF_CELL_MARGINS,
                                verticalAlign: VerticalAlign.CENTER,
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        style: 'MyNormalStyle',
                                        children: [
                                            new TextRun({
                                                text: element,
                                                bold: true,
                                                size: 20,
                                            }),
                                        ],
                                    })
                                ],
                            })
                        }),
                        new TableCell({
                            margins: HEADER_AND_REF_CELL_MARGINS,
                            verticalAlign: VerticalAlign.CENTER,
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    style: 'MyNormalStyle',
                                    children: [
                                        new TextRun({
                                            text: `Remarks`,
                                            bold: true,
                                            size: 20,
                                        }),
                                    ],
                                }),
                            ],
                        })
                    ]
                }),
                // One row per reference document
                ...data.documents.map(document => {
                    // Build the feature presence matrix for this document
                    const documentFeaturesArray = buildUniqueDocumentFeaturesArray(document.features)

                    return new TableRow({
                        children: [
                            // Reference column (document number)
                            new TableCell({
                                margins: HEADER_AND_REF_CELL_MARGINS,
                                verticalAlign: VerticalAlign.CENTER,
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        style: 'MyNormalStyle',
                                        children: [
                                            new TextRun({
                                                text: `${document.documentNumber}`,
                                                bold: true,
                                                size: 20,
                                            }),
                                        ],
                                    })
                                ],
                            }),
                            ...projectFeaturesArray.map(projectFeature => {
                                const hasFeature = documentFeaturesArray.includes(projectFeature)
                                return new TableCell({
                                    margins: FEATURE_CELL_MARGINS,
                                    shading: {
                                        type: ShadingType.SOLID,
                                        color: hasFeature ? "FF3131" : "50C878",
                                    },
                                    verticalAlign: VerticalAlign.CENTER,
                                    children: [
                                        new Paragraph({
                                            alignment: AlignmentType.CENTER,
                                            style: 'MyNormalStyle',
                                            children: [
                                                new TextRun({
                                                    text: hasFeature ? "No" : "Yes",
                                                }),
                                            ],
                                        })
                                    ],
                                })
                            }),
                            new TableCell({
                                margins: HEADER_AND_REF_CELL_MARGINS,
                                verticalAlign: VerticalAlign.CENTER,
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        style: 'MyNormalStyle',
                                        children: [
                                            new TextRun({
                                                text: "",
                                            }),
                                        ],
                                    })
                                ],
                            }),
                        ]
                    })
                })

            ]
        })
    ]
    return elements
}

const disclaimer= () => {
    const elements = [
        createSectionHeading("Disclaimer and Terms of Use"),
        createTextParagraph({
            text: "Riahi Patents have prepared this report to provide technical analysis and insights based on the information available at the time of search. The findings, summaries, and recommendations are intended solely for informational purposes and do not constitute legal advice or legal opinion.",
            alignment: AlignmentType.JUSTIFIED,
            italics: true,
        }),
        createTextParagraph({
            text: "Notice to Client",
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 300, after: 300 },
            bold: true,
        }),
        createBulletParagraph("This report may include information sourced from publicly available and third-party databases, including but not limited to USPTO, EPO, WIPO, Orbit Intelligence, Thomson Innovation, and Google Patents. While care has been taken to ensure accuracy, Riahi Patents makes no warranties regarding the completeness, accuracy, or reliability of third-party data, including translations."),
        createBulletParagraph("All information is provided on an “as-is” basis without guarantees or warranties of any kind, express or implied."),
        createBulletParagraph("Searches are limited to published documents and publicly accessible information at the time the search was conducted."),
        createBulletParagraph("Patent family and legal status information are based on available public records (e.g., INPADOC) and are subject to change."),
        createBulletParagraph("No independent verification of search results beyond the database-sourced content has been performed."),
        createBulletParagraph("Patent life estimates are for reference purposes only and are generally calculated based on filing or priority dates plus statutory terms."),
        createBulletParagraph("Riahi Patents is not a law firm and does not engage in the practice of law. All reports are intended for technical and informational use and are subject to further review by a qualified attorney or licensed patent agent for legal interpretation."),
        createBulletParagraph("No part of this report should be construed as a guarantee of patentability, validity, non-infringement, or freedom-to-operate."),
        createTextParagraph({
            text: "Limitation of Liability",
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 300, after: 300 },
            bold: true,
        }),
        createBulletParagraph("Riahi Patents expressly disclaims any liability for damages, direct or indirect, arising out of or related to the use of this report. Liability is strictly limited to the amount invoiced for the specific service rendered. Riahi Patents is not liable for database errors, omissions, or external disruptions in data services."),
        new Paragraph({
            style: 'MyNormalStyle',
            pageBreakBefore: true,
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 300, after: 300 },
            children: [
                new TextRun({
                    text: "External Content Disclaimer",
                    bold: true,
                }),
            ]
        }),
        createBulletParagraph("This report may reference third-party websites or databases for informational convenience. Riahi Patents does not control the content of such external sources and is not responsible for their accuracy, completeness, or availability."),
    ]
    return elements
}

async function saveDocumentToFile(doc, fileName, mimeType) {
    // Create a mime type that will associate the new file with Microsoft Word
    // const mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    // Create a Blob containing the Document instance and the mimeType
    Packer.toBlob(doc).then(blob => {
      const docblob = blob.slice(0, blob.size, mimeType)
      // Save the file using saveAs from the file-saver package
      saveAs(docblob, fileName)
    })
}

export const generateReportTypeC = async (data) => {
    // console.log(data)
    const doc = new Document({
        creator: "Riahi-IP",
        description: `Report generated for ${data.project.projNum}`,
        title: `${data.project.projNum}`,
        styles: {
            default: {
                heading1: {
                    run: {
                        size: 28,
                        bold: true,
                        italics: false,
                        color: "000000",
                        font: "arial",
                    },
                    paragraph: {
                        spacing: {
                            after: 120,
                        },
                    },
                },
                heading2: {
                    run: {
                        size: 26,
                        bold: true,
                        font: "arial",
                        // underline: {
                        //     type: UnderlineType.DOUBLE,
                        //     color: "000000",
                        // },
                    },
                    paragraph: {
                        spacing: {
                            before: 240,
                            after: 120,
                        },
                        font: "arial",
                    },
                },
                listParagraph: {
                    run: {
                        color: "000000",
                        font: "arial",
                    },
                },
            },
            paragraphStyles: [
                {
                    id: "MySpectacularStyle",
                    name: "My Spectacular Style",
                    basedOn: "Heading1",
                    next: "Heading1",
                    quickFormat: true,
                    run: {
                        italics: true,
                        color: "990000",
                        font: "arial",
                        size: 24,
                    },
                },
                {
                    id: "MyNormalStyle",
                    name: "My Normal Style",
                    basedOn: "Normal",
                    next: "Normal",
                    quickFormat: true,
                    run: {
                        italics: false,
                        color: "000000",
                        font: "arial",
                        size: 24,
                    },
                    paragraph: {
                        // indent: {
                        //     left: convertInchesToTwip(0.5),
                        // },
                        spacing: {
                            line: 276,
                            after: 200,
                        },
                    },
                },
                {
                    id: "MyTableStyle",
                    name: "My Table Style",
                    basedOn: "Normal",
                    next: "Normal",
                    quickFormat: true,
                    run: {
                        italics: false,
                        color: "000000",
                        font: "arial",
                        size: 20,
                    },
                    paragraph: {
                        // indent: {
                        //     left: convertInchesToTwip(0.5),
                        // },
                        spacing: {
                            line: 276,
                        },
                    },
                },
                {
                    id: "MyItalicsStyle",
                    name: "My Italics Style",
                    basedOn: "Normal",
                    next: "Normal",
                    quickFormat: true,
                    run: {
                        italics: true,
                        color: "000000",
                        font: "arial",
                        size: 24,
                    },
                    paragraph: {
                        spacing: {
                            line: 276,
                        },
                    },
                },
            ],
        },
        sections: [
            {
                // COVER PAGE
                properties: {
                    type: SectionType.CONTINUOUS,
                    // add background color (not working)
                    background: {
                        color: "000000",
                    },
                    page: {
                        margin: {
                            top: convertInchesToTwip(3.2),
                            bottom: convertInchesToTwip(0.5),
                            left: convertInchesToTwip(1),
                            right: convertInchesToTwip(0.5),
                        },
                        shading: {
                            type: ShadingType.SOLID,
                            color: "FF3131",
                            // fill: "FF3131",
                        },
                    },
                },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: "Riahi Patents, Inc.",
                                size: 40,
                                bold: true,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 600 },
                        children: [
                            new TextRun({
                                text: "INVALIDITY SEARCH REPORT",
                                size: 56,
                                bold: true,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 1000 },
                        children: [
                            new TextRun({
                                text: `${data.project.projNum}`,
                                size: 36,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 300 },
                        children: [
                            new TextRun({
                                text: `Title: `,
                                size: 36,
                                bold: false,
                                font: "arial",
                            }),
                            new TextRun({
                                text: `${data.project.projTitle}`,
                                size: 36,
                                bold: false,
                                font: "arial",
                                allCaps: true,
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 1800 },
                        children: [
                            new TextRun({
                                text: "Prepared for ",
                                size: 36,
                                font: "arial",
                            }),
                            new TextRun({
                                text: `${data.project.company}`,
                                size: 36,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 400 },
                        children: [
                            new TextRun({
                                text: `${data.project.submissionDate ? new Date(data.project.submissionDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : ""}`,
                                size: 36,
                                font: "arial",
                            }),
                        ],
                    }),
                ],
            },
            {
                // TABLE OF CONTENT
                children: [
                    new TableOfContents("Table of Content", {
                        run: {
                            size: 28,
                            bold: true,
                            italics: false,
                            color: "000000",
                            font: "arial",
                        },
                        hyperlink: true,
                        headingStyleRange: "1-5",
                        stylesWithLevels: [
                            new StyleLevel('MyNormalStyle', 1),
                            // new StyleLevel("MyNormalStyle", 2),
                            // new StyleLevel("MyNormalStyle", 3),
                        ],
                        // entryAndPageNumberSeparator: '',
                    })
                ]
            },
            {
                headers: {
                    default: new Header({
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                    new TextRun({
                                        children: [PageNumber.CURRENT],
                                        font: 'arial',
                                    })
                                ]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.LEFT,
                                spacing: {after: 400 },
                                children: [
                                    // new ImageRun({
                                    //     // data: "https://images.unsplash.com/photo-1530595467537-0b5996c41f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxleHBsb3JlLWZlZWR8MXx8fGVufDB8fHx8fA%3D%3D&w=1000&q=80",
                                    //     transformation: {
                                    //         width: 50,
                                    //         height: 50,
                                    //     },
                                    // }),
                                    new TextRun({
                                        text: `${data.project.projNum}`,
                                        allCaps: true,
                                        font: "arial",
                                        size: 24,
                                        bold: false,
                                    }),
                                    // new TextRun({
                                    //     children: [PageNumber.CURRENT],
                                    // })
                                ]
                            }),
                        ],
                    }),
                },
                footers: {
                    default: new Footer({
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                border: {
                                    top: {
                                        color: 'auto',
                                        space: 1,
                                        style: "single",
                                        size: 10,
                                    }
                                },
                                children: [
                                    new TextRun({
                                        text: "Riahi Patents, Inc.",
                                        size: 24,
                                        font: 'arial',
                                    }),
                                    new TextRun({
                                        text: "                                                                    ",
                                        size: 24,
                                        font: 'arial',
                                    }),
                                    new ExternalHyperlink({
                                        children: [
                                            new TextRun({
                                                text: "https://riahipatents.com",
                                                style: "Hyperlink",
                                                size: 24,
                                                font: 'arial',
                                            }),
                                        ],
                                        link: "https://riahipatents.com",
                                    }),
                                    // new TextRun({
                                    //     children: [PageNumber.CURRENT],
                                    // }),
                                    
                                ]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({
                                        text: "Confidential Document",
                                        size: 24,
                                        font: 'arial',
                                        italics: true,
                                    }),
                                    // new TextRun({
                                    //     children: [PageNumber.CURRENT],
                                    // }),
                                    
                                ]
                            }),
                           
                        ],
                    }),
                },
                // REPORT OBJECTIVE
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: reportObjective()
            },
            {
                // FOCUS OF SEARCH
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: focusOfSearch(data.project.features)
            },
            {
                // SEARCH ANALYSIS SUMMARY
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: searchAnalysisSummary(data.project.features)
            },
            {
                // DELIVERABLES
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: deliverables()
            },
            {
                // PATENT INFORMATION
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: patentInformation()
            },
            {
                // Search Dates and Reference Information
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: searchDatesAndReferenceInformation(data.project.features)
            },
            {
                // PROSECUTION HISTORY ANALYSIS
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: prosecutionHistoryAnalysis()
            },
            {
                // LIST OF REFERENCES IDENTIFIED
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: listOfReferencesIdentified(data.documents)
            },
            {
                // REFERENCE KEY MATRIX TABLE
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: referenceKeyMatrixTable(data)
            },
            {
                // DISCLAIMER
                // type: SectionType.CONTINUOUS,
                style: 'MyNormalStyle',
                children: disclaimer(data)
            },
            {
                // BACK COVER PAGE
                properties: {
                    type: SectionType.NEXT_PAGE,
                    page: {
                        margin: {
                            top: convertInchesToTwip(1.5),
                            left: convertInchesToTwip(1),
                        },
                    },
                },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 },
                        children: [
                            new TextRun({
                                text: "https://riahipatents.com",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 },
                        children: [
                            new TextRun({
                                text: "bit.ly/dr_riahi",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 },
                        children: [
                            new TextRun({
                                text: "projects@riahi-ip.com",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 0, after: 100 },
                        children: [
                            new TextRun({
                                text: "5307 Victoria Drive #267 Vancouver, BC V5P 3V6",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 0, after: 100 },
                        children: [
                            new TextRun({
                                text: "1300-1969 Upper Water St., Mcinnes Cooper Tower, Purdy's Wharf, Halifax, NS B3J 3R7",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 },
                        children: [
                            new TextRun({
                                text: "295 Hagey Blvd., Waterloo, ON, N2L 6R5",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 20 },
                        children: [
                            new TextRun({
                                text: "+1(226)458-8423",
                                size: 30,
                                bold: false,
                                font: "arial",
                            }),
                        ],
                    }),
                ],
            }
        ],
    });
    const projectTitle = (`Invalidity Search Report - ${data.project.projNum.replaceAll('-','.')}.docx`)
    await saveDocumentToFile(doc, projectTitle, "application/vnd.openxmlformats-officedocument.wordprocessingml.document")

    // return Packer.toBuffer(doc).then((buffer) => {
    //     fs.writeFileSync(`${data.project.projNum}.docx`, buffer);
    // });
}